import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  MapPin,
  Clock,
  CheckCircle,
  ShoppingBag,
  UtensilsCrossed,
} from 'lucide-react';
import { useCartStore, useOrderStore } from '../../store/cart';
import { formatPrice } from '../../lib/utils';

type PickupLocation = 'main-stand' | 'pit-lane' | 'grandstand-north' | 'grandstand-south';

const pickupLocations: { id: PickupLocation; name: string; description: string }[] = [
  { id: 'main-stand', name: 'Main Concession Stand', description: 'Near main entrance' },
  { id: 'pit-lane', name: 'Pit Lane Cafe', description: 'Pit building, Level 1' },
  { id: 'grandstand-north', name: 'Grandstand North', description: 'North side food court' },
  { id: 'grandstand-south', name: 'Grandstand South', description: 'South side kiosk' },
];

export function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, checkout, merchandiseItems, foodItems } = useCartStore();
  const { addOrder } = useOrderStore();

  const [step, setStep] = useState<'details' | 'payment' | 'confirmation'>('details');
  const [pickupLocation, setPickupLocation] = useState<PickupLocation>('main-stand');
  const [contactInfo, setContactInfo] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [orderId, setOrderId] = useState<string | null>(null);

  const merchandiseList = merchandiseItems();
  const foodList = foodItems();
  const total = subtotal();

  const hasMerchandise = merchandiseList.length > 0;
  const hasFood = foodList.length > 0;

  if (items.length === 0 && step !== 'confirmation') {
    navigate('/cart');
    return null;
  }

  const handleSubmitDetails = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('payment');
  };

  const handlePayment = () => {
    // Simulate payment processing
    const id = addOrder({
      items: [...items],
      subtotal: total,
      total: total,
      status: 'confirmed',
      type: hasMerchandise && hasFood ? 'mixed' : hasMerchandise ? 'merchandise' : 'food',
      pickupLocation: pickupLocations.find((l) => l.id === pickupLocation)?.name,
    });
    setOrderId(id);
    checkout();
    setStep('confirmation');
  };

  if (step === 'confirmation') {
    return (
      <div className="py-12">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Order Confirmed!</h1>
          <p className="text-gray-600 mb-8">
            Thank you for your order. You'll receive a confirmation email shortly.
          </p>

          <div className="card p-6 text-left mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Order ID</span>
              <span className="font-mono font-bold">{orderId}</span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Total</span>
              <span className="font-bold text-lg">{formatPrice(total)}</span>
            </div>
            {hasFood && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Pickup Location</span>
                <span className="font-medium">
                  {pickupLocations.find((l) => l.id === pickupLocation)?.name}
                </span>
              </div>
            )}
          </div>

          {hasFood && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-2 text-orange-800 font-medium mb-2">
                <Clock size={18} />
                Estimated Ready Time
              </div>
              <p className="text-2xl font-bold text-orange-900">10-15 minutes</p>
              <p className="text-sm text-orange-700 mt-1">
                We'll notify you when your order is ready for pickup
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Link
              to="/map"
              className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
            >
              <MapPin size={20} />
              Find Pickup Location
            </Link>
            <Link
              to="/"
              className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => (step === 'payment' ? setStep('details') : navigate('/cart'))}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            {step === 'payment' ? 'Back to Details' : 'Back to Cart'}
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-8">
          <div
            className={`flex items-center gap-2 ${
              step === 'details' ? 'text-red-600' : 'text-green-600'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                step === 'details' ? 'bg-red-600' : 'bg-green-600'
              }`}
            >
              {step === 'details' ? '1' : '✓'}
            </div>
            <span className="font-medium">Details</span>
          </div>
          <div className="flex-1 h-0.5 bg-gray-200" />
          <div
            className={`flex items-center gap-2 ${
              step === 'payment' ? 'text-red-600' : 'text-gray-400'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                step === 'payment' ? 'bg-red-600' : 'bg-gray-300'
              }`}
            >
              2
            </div>
            <span className="font-medium">Payment</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            {step === 'details' && (
              <form onSubmit={handleSubmitDetails} className="space-y-6">
                {/* Contact Info */}
                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Contact Information
                  </h2>
                  <div className="grid gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        required
                        value={contactInfo.name}
                        onChange={(e) =>
                          setContactInfo({ ...contactInfo, name: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          required
                          value={contactInfo.phone}
                          onChange={(e) =>
                            setContactInfo({ ...contactInfo, phone: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          required
                          value={contactInfo.email}
                          onChange={(e) =>
                            setContactInfo({ ...contactInfo, email: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pickup Location (for food orders) */}
                {hasFood && (
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      <MapPin size={20} className="inline mr-2" />
                      Pickup Location
                    </h2>
                    <div className="space-y-3">
                      {pickupLocations.map((location) => (
                        <label
                          key={location.id}
                          className={`flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${
                            pickupLocation === location.id
                              ? 'border-red-600 bg-red-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="pickup"
                            value={location.id}
                            checked={pickupLocation === location.id}
                            onChange={(e) =>
                              setPickupLocation(e.target.value as PickupLocation)
                            }
                            className="sr-only"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{location.name}</p>
                            <p className="text-sm text-gray-500">{location.description}</p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              pickupLocation === location.id
                                ? 'border-red-600'
                                : 'border-gray-300'
                            }`}
                          >
                            {pickupLocation === location.id && (
                              <div className="w-2.5 h-2.5 bg-red-600 rounded-full" />
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
                >
                  Continue to Payment
                </button>
              </form>
            )}

            {step === 'payment' && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    <CreditCard size={20} className="inline mr-2" />
                    Payment Method
                  </h2>

                  {/* Mock payment form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Card Number
                      </label>
                      <input
                        type="text"
                        placeholder="4242 4242 4242 4242"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry
                        </label>
                        <input
                          type="text"
                          placeholder="MM/YY"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CVC
                        </label>
                        <input
                          type="text"
                          placeholder="123"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  className="w-full py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <CreditCard size={20} />
                  Pay {formatPrice(total)}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  This is a demo checkout. No actual payment will be processed.
                </p>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {merchandiseList.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <ShoppingBag size={14} />
                      Merchandise
                    </div>
                    {merchandiseList.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm py-1"
                      >
                        <span className="text-gray-600">
                          {item.quantity}x {item.name}
                        </span>
                        <span>{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {foodList.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <UtensilsCrossed size={14} />
                      Food & Drinks
                    </div>
                    {foodList.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm py-1"
                      >
                        <span className="text-gray-600">
                          {item.quantity}x {item.name}
                        </span>
                        <span>{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
